import { Role, ActiveLicenses, OrganisationClaim } from '@/core/types';
import { StateCreator } from 'zustand';

interface User {
    homeAccountId: string;
    name: string;
    username: string;
    givenName: string;
    familyName: string;
    fullName: string;
    email: string;
    role: Role;
    organisation: OrganisationClaim;
    accessToAllStores: boolean;
    shopIds: string[];
    organisationClaims?: OrganisationClaim[];
}

const getStoredUser = (): User | null => {
    try {
        const stored = sessionStorage.getItem('user');
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed?.homeAccountId && parsed?.email) {
                return parsed;
            }
        }
    } catch (error) {
        sessionStorage.removeItem('user');
    }
    return null;
};

// Helper function to handle both single and array of licenses
function toArray(licenses: number | number[]): number[] {
    if (Array.isArray(licenses)) {
        return licenses;
    }
    return [licenses];
}

export interface AuthSlice {
    user: User | null;
    setUser: (user: User | null) => void;
    updateOrganisation: (orgId: string) => void;
    updateOrganisationClaim: (claim: OrganisationClaim) => void;
    hasShopAccess: (shopIds: string) => boolean;
    hasLicense: (licenses: ActiveLicenses | ActiveLicenses[]) => boolean;
    hasRole: (role: Role) => boolean;
    clearAuth: () => void;
    clearCache: () => void;
}

export const createAuthSlice: StateCreator<AuthSlice> = (set, get) => ({
    user: getStoredUser(),

    setUser: (user) => {
        set({ user });
        if (user) {
            try {
                sessionStorage.setItem('user', JSON.stringify(user));
                if (user.organisation?.id) {
                    localStorage.setItem('selectedOrgId', user.organisation.id);
                }
            } catch (error) {
                console.error('Failed to store user:', error);
            }
        } else {
            try {
                sessionStorage.removeItem('user');
            } catch (error) {
                console.error('Failed to remove user from storage:', error);
            }
        }
    },

    updateOrganisation: (orgId) => {
        const { user } = get();
        if (!user || !user.organisationClaims) return;
        const org = user.organisationClaims.find((o) => o.id === orgId);
        if (!org) return;
        const updated: User = { ...user, organisation: org };
        set({ user: updated });
        try {
            sessionStorage.setItem('user', JSON.stringify(updated));
            localStorage.setItem('selectedOrgId', orgId);
        } catch (error) {
            console.error('Failed to store user after org update:', error);
        }
    },

    updateOrganisationClaim: (claim) => {
        const { user } = get();
        if (!user) return;
        const updatedClaims =
            user.organisationClaims?.map((o) => (o.id === claim.id ? claim : o)) ?? user.organisationClaims;

        const updatedOrganisation = user.organisation?.id === claim.id ? claim : user.organisation;

        const updated: User = { ...user, organisationClaims: updatedClaims, organisation: updatedOrganisation };
        set({ user: updated });
        try {
            sessionStorage.setItem('user', JSON.stringify(updated));
        } catch (error) {
            console.error('Failed to store user after license update:', error);
        }
    },

    /**
     * Checks if the current user has access to a specific shop.
     * @param shopId the shop ID to be checked against the current user's shop IDs.
     * @returns true if the user has access to the shop, else false.
     */
    hasShopAccess: (shopId) => {
        const { user } = get();
        if (!user || !user.shopIds) return false;
        if (user.accessToAllStores || user.role === Role.SuperAdmin) return true;

        return user.shopIds?.includes(shopId);
    },

    hasLicense: (licenses) => {
        const { user } = get();
        if (!user) return false;

        const activeLicenses = user.organisation?.activeLicenses ?? [];

        // Handle both array and and single license: hasLicense(ShopAutomation) or hasLicense([ShopAutomation, Rfid])
        const licensesArray = Array.isArray(licenses) ? licenses : [licenses];

        // Check if user has ANY of the provided licenses
        return licensesArray.some((license) => license === ActiveLicenses.Basic || activeLicenses.includes(license));
    },

    /**
     * Checks if the current user has a specific role or a role which is higher in the hierarchy.
     * @param role the role to be checked against the current users role.
     * @returns true if the user has a role with same or higher hierarchy, else false.
     */
    hasRole: (role) => {
        const { user } = get();
        if (!user) return false;

        switch (user.role) {
            case Role.SuperAdmin:
                return true;
            case Role.OrganisationAdmin:
                return (
                    role == Role.User ||
                    role == Role.StoreAdmin ||
                    role == Role.UnitAdmin ||
                    role == Role.OrganisationAdmin
                );
            case Role.UnitAdmin:
                return role == Role.User || role == Role.StoreAdmin || role == Role.UnitAdmin;
            case Role.StoreAdmin:
                return role == Role.User || role == Role.StoreAdmin;
            case Role.User:
                return role == Role.User;
            default:
                return false;
        }
    },

    clearCache: () => {
        const authKeys = ['selectedOrgId', 'wanzl-connect-store', 'language', 'darkMode', 'cardsTableView'];
        authKeys.forEach((key) => {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                console.warn(`[AuthSlice] Failed to remove ${key}:`, error);
            }
        });
    },

    clearAuth: () => {
        set({ user: null });
        try {
            sessionStorage.removeItem('user');
        } catch (error) {
            console.warn('[AuthSlice] Failed to remove user from sessionStorage:', error);
        }
    }
});
